<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'
import { useAnchoredPosition, type AnchorPlacement } from '../AppPopover/use-anchored-position'
const props = withDefaults(
  defineProps<{
    /** Tooltip text. Ignored when the `content` slot is used; with neither, the tooltip never opens. */
    content?: string
    placement?: AnchorPlacement
    offset?: number
    delay?: number
    /** Visual treatment: `plain` compact dark pill (default) · `rich` card surface for structured slot content · `callout` the rich card at resting elevation, for a tooltip that belongs to the page. */
    variant?: 'plain' | 'rich' | 'callout'
    /** Shows or hides the tooltip from outside; hover and focus then only emit `update:open`. Leave unset for the tooltip to follow hover and focus itself. */
    open?: boolean
  }>(),
  {
    content: '',
    placement: 'top',
    offset: 8,
    delay: 200,
    variant: 'plain',
    open: undefined,
  },
)

const emit = defineEmits<{
  /** Hover or focus asked to show (`true`) or hide (`false`) the tooltip. */
  'update:open': [open: boolean]
}>()

const tooltipId = useId()
const descriptionId = useId()
const hovered = ref(false)
const open = computed(() => props.open ?? hovered.value)

function request(next: boolean) {
  hovered.value = next
  emit('update:open', next)
}

const anchorRef = ref<HTMLElement | null>(null)
const tooltipRef = ref<HTMLElement | null>(null)

const { floatingStyles, start, stop } = useAnchoredPosition(anchorRef, tooltipRef, () => ({
  placement: props.placement,
  offset: props.offset,
  padding: 8,
}))

let showTimer: ReturnType<typeof setTimeout> | undefined

function scheduleShow() {
  clearTimeout(showTimer)
  showTimer = setTimeout(() => {
    request(true)
  }, props.delay)
}

function showNow() {
  clearTimeout(showTimer)
  request(true)
}

function hide() {
  clearTimeout(showTimer)
  request(false)
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') hide()
}

watch(
  open,
  (isOpen) => {
    if (typeof window === 'undefined') return
    if (isOpen) nextTick(start)
    else stop()
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  clearTimeout(showTimer)
  stop()
})
</script>

<template>
  <span
    ref="anchorRef"
    class="contents"
    @mouseenter="scheduleShow"
    @mouseleave="hide"
    @focusin="showNow"
    @focusout="hide"
    @keydown="onKeydown"
  >
    <slot
      name="trigger"
      :aria="{ 'aria-describedby': !$slots.content && content ? descriptionId : tooltipId }"
    />
    <span v-if="!$slots.content && content" :id="descriptionId" class="sr-only">{{ content }}</span>

    <Teleport to="body">
      <Transition
        enter-active-class="transition-[opacity,transform] duration-fast ease-soft motion-reduce:transition-none"
        enter-from-class="opacity-0 translate-y-1"
        leave-active-class="transition-[opacity,transform] duration-fast ease-soft-exit motion-reduce:transition-none"
        leave-to-class="opacity-0 translate-y-1"
      >
        <div
          v-if="open && ($slots.content || content)"
          :id="tooltipId"
          ref="tooltipRef"
          role="tooltip"
          :style="floatingStyles"
          :class="[
            'pointer-events-none z-50',
            variant === 'plain'
              ? 'max-w-64 rounded-2 bg-text-primary px-6 py-2 text-caption text-bg-primary shadow-3'
              : 'min-w-64 rounded-4 bg-bg-primary p-8 inset-ring-1 inset-ring-border',
            variant === 'rich' && 'shadow-3',
            variant === 'callout' && 'shadow-1',
          ]"
        >
          <slot name="content">{{ content }}</slot>
        </div>
      </Transition>
    </Teleport>
  </span>
</template>
